import React, { useState, useEffect } from "react";
import emailjs from "emailjs-com";
import { FaPhone, FaEnvelope, FaMapMarkerAlt, FaClock } from "react-icons/fa";
import { motion } from "framer-motion";
import AOS from "aos";
import "aos/dist/aos.css";
import "./Contact.css";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const [status, setStatus] = useState({ message: "", type: "" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    AOS.init({ duration: 1000 });
    emailjs.init("P51a518TKmlzb8vFU");
  }, []);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setStatus({ message: "Sending...", type: "info" });

    emailjs
      .sendForm(
        "service_99hu8md",
        "template_3aw7b9b",
        e.target,
        "HczC6KBNCYSaWtZaq"
      )
      .then(
        (result) => {
          console.log("Success:", result.text);
          setStatus({
            message: "Message sent successfully! CA Madhuri will contact you soon.",
            type: "success",
          });
          setFormData({ name: "", email: "", phone: "", message: "" });
        },
        (error) => {
          console.error("Error:", error.text);
          setStatus({ message: "Failed to send message. Please try again.", type: "error" });
        }
      )
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    // Single scoped wrapper class — every rule in Contact.css is
    // nested under .camadhuri-contact so outside CSS can't leak in
    // and this component can't leak out either.
    <div className="camadhuri-contact">
      <div className="contact-container">
        {/* Hero Section */}
        <motion.div
          className="contact-hero"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <div>
            <h1>Get in Touch with CA Madhuri</h1>
            <p>We're here to help you achieve financial excellence</p>
          </div>
        </motion.div>

        <div className="contact-content-section">
          <div className="contact-row">
            {/* Contact Form */}
            <motion.div
              className="contact-form-column"
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-aos="fade-right"
            >
              <div className="contact-form-card">
                <h3>Send Us a Message</h3>
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="contact-form-group">
                    <label htmlFor="name">Name</label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      placeholder="Your Name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="email">Email</label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      placeholder="Your Email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="phone">Phone</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      placeholder="Your Phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div className="contact-form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      placeholder="Your Message"
                      value={formData.message}
                      onChange={handleChange}
                      required
                    ></textarea>
                  </div>

                  <motion.button
                    type="submit"
                    disabled={loading}
                    className="contact-submit-btn"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </motion.button>
                </form>

                {status.message && (
                  <motion.p
                    className={`status-message status-${status.type}`}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    {status.message}
                  </motion.p>
                )}
              </div>
            </motion.div>

            {/* Contact Details */}
            <motion.div
              className="contact-info-column"
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
              data-aos="fade-left"
            >
              {/* Address */}
              <motion.div className="contact-info-item" whileHover={{ x: 10 }}>
                <div className="contact-info-icon">
                  <FaMapMarkerAlt />
                </div>
                <div className="contact-info-content">
                  <h4>Office Address</h4>
                  <p>55B Narsi vihar</p>
                  <p>near Mandi chauraha</p>
                  <p>Mathura, Uttar Pradesh 281004</p>
                </div>
              </motion.div>

              {/* Phone */}
              <motion.div className="contact-info-item" whileHover={{ x: 10 }}>
                <div className="contact-info-icon">
                  <FaPhone />
                </div>
                <div className="contact-info-content">
                  <h4>Phone Number</h4>
                  <p>
                    <a href="tel:+91-7983428842">+91 79834 28842</a>
                  </p>
                </div>
              </motion.div>

              {/* Email */}
              <motion.div className="contact-info-item" whileHover={{ x: 10 }}>
                <div className="contact-info-icon">
                  <FaEnvelope />
                </div>
                <div className="contact-info-content">
                  <h4>Email Address</h4>
                  <p>
                    <a href="mailto:camadhurico@gmail.com">camadhurico@gmail.com</a>
                  </p>
                </div>
              </motion.div>

              {/* Hours */}
              <motion.div className="contact-info-item" whileHover={{ x: 10 }}>
                <div className="contact-info-icon">
                  <FaClock />
                </div>
                <div className="contact-info-content">
                  <h4>Business Hours</h4>
                  <p>Monday - Friday: 9:00 AM - 6:00 PM</p>
                  <p>Saturday: 10:00 AM - 4:00 PM</p>
                  <p>Sunday: Closed</p>
                </div>
              </motion.div>

              {/* Google Map */}
              <motion.div
                className="contact-map-container"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <iframe
                  title="CA Madhuri Location"
                  className="contact-map"
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d299.92145467336127!2d77.64851977716853!3d27.4883564342332!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x397373e0e91802a1%3A0x25150d9381b3f226!2sNarsi%20Vihar%20Park!5e0!3m2!1sen!2sin!4v1783241987946!5m2!1sen!2sin"
                  allowFullScreen=""
                  loading="lazy"
                ></iframe>
              </motion.div>
            </motion.div>
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          className="contact-cta-section"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <h2>Why Contact CA Madhuri?</h2>
          <div className="contact-cta-grid">
            <motion.div className="contact-cta-item" whileHover={{ scale: 1.05 }}>
              <p>✓ 3+ Years Experience</p>
            </motion.div>
            <motion.div className="contact-cta-item" whileHover={{ scale: 1.05 }}>
              <p>✓ 50+ Satisfied Clients</p>
            </motion.div>
            <motion.div className="contact-cta-item" whileHover={{ scale: 1.05 }}>
              <p>✓ Personalized Solutions</p>
            </motion.div>
            <motion.div className="contact-cta-item" whileHover={{ scale: 1.05 }}>
              <p>✓ 24/7 Support</p>
            </motion.div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;